import { useMemo } from 'react'
import { isEqual } from 'lodash'
import {
  findOneQuestion_findOneQuestion_answer as Answer,
  findOneQuestion_findOneQuestion as Question,
} from '__generatedTypes__/findOneQuestion'
import { QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { FormStateApi } from './consts'
import { convertAnswerToRxWritingAnswer } from './RxWritingAnswers/helpers'
import { convertAnswerToNumericResponseAnswer, NumericResponseAnswer } from './NumericResponseAnswer/helpers'
import { convertAnswerToTextCompletionAnswer, TextCompletionBlank } from './TextCompletionAnswers/helpers'
import { getQuestionTypeConfig } from './questionTypeConfig'

const shouldResetMultipleAnswerStatistics = (initialCorrectAnswers?: Answer[], correctAnswers?: Answer[]) => {
  const cb = (answer: Answer) => ({
    point: Number(answer.point),
  })

  const shouldResetStatistics = !isEqual(initialCorrectAnswers?.map(cb), correctAnswers?.map(cb))

  return shouldResetStatistics
}

const normalizeNumericResponseAnswer = (answer?: NumericResponseAnswer) =>
  answer?.isFraction
    ? {
        isFraction: true,
        numeratorValue: Number(answer.numeratorValue),
        denominatorValue: Number(answer.denominatorValue),
      }
    : {
        isFraction: false,
        value: Number(answer?.value),
      }

const normalizeTextCompletionAnswer = (blanks?: TextCompletionBlank[]) =>
  blanks?.map(({ options }) => ({
    correctOptionIds: options?.filter(({ isCorrect }) => isCorrect).map(({ id }) => id),
  }))

export const useAnswerChange = (setIsResetStatisticsDisabled: (isDisabled: boolean) => void, question?: Question) => {
  const [
    initialCorrectAnswers,
    initialRxWritingAnswers,
    initialNumericResponseAnswer,
    initialTextCompletionAnswer,
    initialCorrectAnswerIds,
  ] = useMemo(() => {
    const initialCorrectAnswers = question?.answer?.filter(({ isCorrect }) => isCorrect)
    const initialRxWritingAnswers =
      question?.type === QuestionTypeEnum.RxWritingNDEB ? convertAnswerToRxWritingAnswer(question?.answer) : []
    const initialNumericResponseAnswer =
      question?.type === QuestionTypeEnum.NumericResponse
        ? convertAnswerToNumericResponseAnswer(question?.answer)
        : undefined
    const initialTextCompletionAnswer =
      question?.type === QuestionTypeEnum.TextCompletion
        ? convertAnswerToTextCompletionAnswer(question?.answer)
        : undefined
    const initialCorrectAnswerIds = initialCorrectAnswers?.map(({ id }) => id)

    return [
      initialCorrectAnswers,
      initialRxWritingAnswers,
      initialNumericResponseAnswer,
      initialTextCompletionAnswer,
      initialCorrectAnswerIds,
    ]
  }, [question])

  const handleAnswerChange = (form: FormStateApi) => {
    if (!initialCorrectAnswerIds?.length) {
      return
    }

    const type = form.getFieldState('type')?.value
    const config = getQuestionTypeConfig(type)

    const resetStatistics = (shouldResetStatistics: boolean) => {
      setIsResetStatisticsDisabled(shouldResetStatistics)

      if (shouldResetStatistics) {
        form.change('resetStatistics', true)
      }

      return
    }

    if (config.answerMode === 'json') {
      if (type === QuestionTypeEnum.RxWritingNDEB) {
        const rxAnswerValue = form.getFieldState('rxWritingAnswer')?.value
        const isCorrectAnswerChanged = !isEqual(initialRxWritingAnswers, rxAnswerValue)

        return resetStatistics(isCorrectAnswerChanged)
      }

      if (type === QuestionTypeEnum.NumericResponse) {
        const numericResponseAnswer = form.getFieldState('numericResponseAnswer')?.value
        const isCorrectAnswerChanged = !isEqual(
          normalizeNumericResponseAnswer(initialNumericResponseAnswer),
          normalizeNumericResponseAnswer(numericResponseAnswer)
        )

        return resetStatistics(isCorrectAnswerChanged)
      }

      if (type === QuestionTypeEnum.TextCompletion) {
        const textCompletionAnswer = form.getFieldState('textCompletionAnswer')?.value
        const isCorrectAnswerChanged = !isEqual(
          normalizeTextCompletionAnswer(initialTextCompletionAnswer),
          normalizeTextCompletionAnswer(textCompletionAnswer)
        )

        return resetStatistics(isCorrectAnswerChanged)
      }
    }

    const answerValue = form.getFieldState('answer')?.value
    const correctAnswers = answerValue?.filter(({ isCorrect }) => isCorrect)
    const currentCorrectAnswerIds = correctAnswers?.map(({ id }) => id)

    if (config.hasPoints) {
      const shouldResetStatistics = shouldResetMultipleAnswerStatistics(
        initialCorrectAnswers,
        correctAnswers as Answer[]
      )

      if (shouldResetStatistics) {
        return resetStatistics(true)
      }
    }

    const isCorrectAnswerChanged = Boolean(
      initialCorrectAnswerIds.length !== currentCorrectAnswerIds?.length ||
        initialCorrectAnswerIds.some((id) => !currentCorrectAnswerIds.includes(id))
    )

    return resetStatistics(isCorrectAnswerChanged)
  }

  return {
    initialCorrectAnswers,
    initialCorrectAnswerIds,
    onAnswerChange: handleAnswerChange,
  }
}
